import * as d3 from 'd3';
import _ from 'underscore';

import asm from 'assembly/Asm';
import {assertAndContinue} from 'Asserts';
import {Sequence} from 'biojs/Sequence';
import {AxisLayout, CircularLayout} from 'molbio/maps/circular/CircularLayout';
import {axisSpacing} from 'molbio/maps/circular/CircularUtil';
import Display from 'molbio/maps/circular/Display';
import {HomologyAwareFragmentResultData} from 'molbio/maps/circular/HomologyUtil';
import {RingComponentOptions} from 'molbio/maps/circular/RingComponent';
import {RingView} from 'molbio/maps/circular/RingView';
import * as sequtil from 'molbio/SeqUtil';
import {getBaseWidthStripeParams} from 'molbio/visualization/FragmentVisualizationUtil';
import {toStandardAngle} from 'util/geometry/GeometryUtil';

interface AxisViewOptions extends RingComponentOptions<AxisLayout> {
  circularLayout: CircularLayout;
}

class CircularAxisView extends RingView<AxisLayout> {
  _sequence!: Sequence;
  circularLayout!: CircularLayout;

  static initClass(): void {
    this.prototype.className = 'axis';
  }

  redrawPlasmid(): boolean {
    return true;
  }

  computeInnerRadius(outerRadius: number): number {
    return outerRadius - 22;
  }

  triggerRedraw(): void {
    this.model.trigger('invalidateLayers');
    super.triggerRedraw();
  }

  initialize(options: AxisViewOptions): void {
    super.initialize(options);
    this.circularLayout = options.circularLayout;
    this._sequence = this.model.get('sequence').get('_sequence');
    this.listenTo(this._sequence, 'change:finalized change:fragmentResults', () => {
      this.update();
    });
    assertAndContinue(this._sequence.startIndex() === 1, 'CircularAxisView rendering with startIndex != 1');
  }

  render(): this {
    const root = d3.select(this.el);
    root.append('g').classed('axis-labels', true);
    this.update();
    return this;
  }

  getPiecesData(): Array<HomologyAwareFragmentResultData> | null {
    if (!this._sequence.finalized()) {
      return asm
        .of(this._sequence)
        .getFragmentResultPieces(this._sequence)
        .map(([color, start, end]) => ({
          pieceStartAngle: this.basePairToAngle(start),
          pieceEndAngle: this.basePairToAngle(end),
          color,
        }));
    }
    return null;
  }

  redraw(): void {
    let data: Array<number>;
    let incr: number;
    let range: number;
    if (this.layout.get('visible')) {
      this.$el.show();
    } else {
      this.$el.hide();
      this.layout.set('innerRadius', this.getOuterRadius());
      return;
    }

    this.layout.set('innerRadius', this.computeInnerRadius(this.getOuterRadius()));

    const length = this.model.get('sequence').length();
    const tickSize = this.layout.get('tickSize');
    if (this.model.get('fullCircleView')) {
      incr = axisSpacing(length);
      data = d3.range(incr, length + 1, incr);
    } else {
      let leftBP;
      let rightBP;
      if (this.model.get('leftBP') > this.model.get('rightBP')) {
        range = length - this.model.get('leftBP') + (this.model.get('rightBP') - 0);
        incr = axisSpacing(range);
        leftBP = this.model.get('leftBP') - (this.model.get('leftBP') % incr);
        rightBP = this.model.get('rightBP') - (this.model.get('rightBP') % incr) + incr;
        data = d3.range(leftBP, length - incr / 2, incr).concat(d3.range(incr, rightBP, incr));
      } else {
        range = this.model.get('rightBP') - this.model.get('leftBP');
        incr = axisSpacing(range);
        leftBP = this.model.get('leftBP') - (this.model.get('leftBP') % incr);
        rightBP = this.model.get('rightBP') - (this.model.get('rightBP') % incr) + incr;
        data = d3.range(leftBP, rightBP + 1, incr);
      }
    }

    // eslint-disable-next-line no-restricted-syntax
    const hasOne = _.indexOf(data, 1, true) !== -1;
    // @ts-expect-error: this looks like a noop?
    data = _.union(data, 1);

    // Refresh data, and recompute layers.`
    const root = d3.select(this.el);
    const radius = this.getOuterRadius();
    const label = root.select('.axis-labels').selectAll('.axis-label').data(data);
    const enter = label.enter().append('g').classed('axis-label', true);
    const enterText = enter
      .append('text')
      .attr('text-anchor', 'middle')
      .attr('font-weight', 900)
      .attr('font-size', this.layout.get('type').fontSize);
    enterText.append('svg:textPath').classed('text-path', true);
    enter.append('path').attr('stroke', 'none').attr('stroke-width', 1).attr('fill', 'none');
    enter.append('line').attr('stroke', 'black').attr('shape-rendering', 'geometricPrecision');
    label.exit().remove();

    const pathArc = d3.lineRadial().curve(d3.curveBasis);

    const ref = this;
    const pieces = this.getPiecesData();
    const {useBaseWidthStripe} = getBaseWidthStripeParams(
      ref._sequence.length(),
      ref.getOuterRadius(),
      (basePair) => ref.basePairToAngle(basePair)
    );
    label.each(function (d: number) {
      const standardAngle = ref.basePairToAngle(sequtil.displayToModel(d, ref._sequence.startIndex()));

      const pt1 = Display.polar(
        d === 1 ? radius - tickSize - ref.layout.get('type').height() : radius - tickSize,
        standardAngle
      );
      const pt2 = Display.polar(d === 1 ? radius : radius, standardAngle);

      const path = d3.select(this).select('path');

      let pathArcRadius: number;
      const anglePadding = Math.min(ref.layout.get('type').width(String(d).length * 2) / radius, Math.PI);
      if (Math.PI / 2 < standardAngle && standardAngle < (3 * Math.PI) / 2) {
        data = d3.range(standardAngle + anglePadding, standardAngle - anglePadding, -anglePadding / 4);
        pathArcRadius = radius - tickSize;
        pathArc.radius(pathArcRadius); // + ref.layout.get('type').height() - 2)
      } else {
        data = d3.range(standardAngle - anglePadding, standardAngle + anglePadding, anglePadding / 4);
        pathArcRadius = radius - tickSize - ref.layout.get('type').height() + 2;
        pathArc.radius(pathArcRadius);
      }

      path
        .datum(data.map((angle) => [angle, pathArcRadius]) satisfies Array<[number, number]>)
        .attr('d', pathArc)
        .attr('id', `axis-${d}-${ref.cid}`);

      d3.select(this)
        .select('text')
        .select('.text-path')
        .attr('xlink:href', `#axis-${d}-${ref.cid}`)
        .attr('startOffset', pathArcRadius * anglePadding)
        .text(d === 1 && !hasOne ? '' : d);
      d3.select(this)
        .select('line')
        .attr('stroke-width', d === 1 ? 4 : 2)
        // @ts-expect-error this thinks it can be undefined or stringy for some reason??
        .attr('stroke', (d: number) => {
          if (pieces != null) {
            if (useBaseWidthStripe) {
              // Make the stripe the color of the 'next' base, which is what users think of the number as
              // corresponding to.
              for (const fragmentResult of ref._sequence.fragmentResults().models) {
                const modelBase = sequtil.displayToModel(d, ref._sequence.startIndex());
                if (sequtil.baseInRange(modelBase, fragmentResult.start()!, fragmentResult.end()!)) {
                  if (
                    fragmentResult.homologyRegionOtherColor() != null &&
                    // Start with the 'other' color.
                    (modelBase - fragmentResult.start()!) % 2 === 0
                  ) {
                    return fragmentResult.homologyRegionOtherColor();
                  } else {
                    return fragmentResult.color();
                  }
                }
              }
            } else {
              // Stripes do not correspond nicely to base pair indices and we have to do arithmetic :(
              for (const {pieceStartAngle, pieceEndAngle, color} of pieces) {
                const standardStart = toStandardAngle(pieceStartAngle);
                const standardEnd = toStandardAngle(pieceEndAngle);
                const isTickContainedInNonCrossOriginPiece =
                  standardStart < standardEnd &&
                  standardStart < standardAngle &&
                  standardAngle <= standardEnd;
                // We have to check both sides of the origin separately in a cross-origin piece. That
                // 0 < standardAngle < 2 * PI is enforced by toStandardAngle.
                const isTickContainedInCrossOriginPiece =
                  standardEnd < standardStart &&
                  (standardStart < standardAngle || standardAngle <= standardEnd);
                if (
                  isTickContainedInNonCrossOriginPiece ||
                  isTickContainedInCrossOriginPiece ||
                  // Piece includes entire sequence - single-slot assembly wizard case
                  standardStart === standardEnd
                ) {
                  return color;
                }
              }
            }
          }
          return 'black';
        })
        .attr('shape-rendering', 'geometricPrecision')
        .attr('x1', pt1.x)
        .attr('y1', pt1.y)
        .attr('x2', pt2.x)
        .attr('y2', pt2.y);
    });
  }

  clickAction(e: Event, d: unknown): void {}

  dblClickAction(e: Event, d: unknown): void {}
}

CircularAxisView.initClass();

export default CircularAxisView;
